import React, { useEffect, useState } from 'react';
import { Button } from '@clariness/button';
import { Flex } from '@clariness/flex';
import { FormattedMessage } from 'react-intl';
import { Services } from 'services';
import { useUpdatePatientStatusContext } from 'components/modals/UpdatePatientStatusModal';
import { ReferralStatus, ReferralStatusType } from 'global/referralStatus';
import { addMinutes } from 'date-fns';
import { ShouldRender } from 'components/ShouldRender';
import { useMultiStep } from 'components/MultiStep';
import { convertDateToTargetTimeZone } from 'utils/format/convertDateToTargetTimeZone';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Store } from 'store';
import { ArrowIcon } from '@clariness/icon';
import { useGetStudySitePerformance } from 'services/study-site-performance/get-study-site-performance/hook';

export const UpdatePatientStatusFooter: React.FC = () => {
  const { user } = Store.useUser();
  const {
    id,
    selectedStatus,
    status,
    appointmentScheduleDate,
    appointmentScheduleTime,
    initialAppointmentDate,
    initialAppointmentTime,
    currentConsentedDate,
    closeModal,
    selectedProtocol,
    consentedDate,
    currentIsVisited,
    setCurrentIsVisited,
    canNextStep,
    setCanNextStep,
    parentStatus,
    setParentStatus,
    setSelectedStatus,
    isNewAppointmentNeeded,
    possibleNextStep,
    refetchHistory,
    protocolsData,
    setSelectedProtocol,
    setConsentedDate,
    timezoneData,
    referral,
    selectedDropReason,
    setSelectedDropReason,
    isExternalReferral,
    sponsorPatientID,
    setSponsorPatientID,
  } = useUpdatePatientStatusContext();

  const siteTimeZoneName = timezoneData?.siteTimeZone?.zoneName;

  const toIsoInSiteTimeZone = (date: Date | undefined) =>
    date && siteTimeZoneName
      ? convertDateToTargetTimeZone(date, siteTimeZoneName).toISOString()
      : undefined;

  const { openModal } = Store.useModal();

  const callbackTimeslotSiteSelectedEnabled = useFeatureFlagEnabled(
    'CALLBACK_TIMESLOT_SITE_SELECTED_ENABLED'
  );

  const dropReasons = referral?.screener?.dropReasonsAtSite;

  const failedIECriteriaDropReasons = dropReasons?.length
    ? dropReasons.filter((dr) => dr.statusType === 'IE_CRITERIA')
    : [];

  const notInterestedDropReasons = dropReasons?.length
    ? dropReasons.filter((dr) => dr.statusType === 'NOT_INTERESTED')
    : [];

  const { nextStep, prevStep, activeStep } = useMultiStep();
  const { getStudySitePerformanceAPI } = useGetStudySitePerformance(
    undefined,
    selectedStatus,
    referral,
    appointmentScheduleDate
  );

  const { refetch: refetchProtocol } =
    Services.Referrals.useGetReferralProtocol(id as string, {
      enabled: !isExternalReferral && Boolean(id),
    });

  const showNotificationToUser =
    user?.showNotification &&
    selectedStatus &&
    (ReferralStatus[selectedStatus] === ReferralStatus.APPOINTMENT_SCHEDULED ||
      selectedStatus === ReferralStatus.CONSENTED);

  const { updateReferralSponsorPatientIdAPI } =
    Services.Referrals.useUpdateReferralSponsorPatientId();

  const saveSponsorPatientId = () => {
    if (sponsorPatientID && sponsorPatientID !== referral?.sponsorPatientID) {
      updateReferralSponsorPatientIdAPI({
        referralId: id as string,
        sponsorPatientID,
      });
    }
  };

  const { updateStatus, isLoading } =
    Services.Referrals.useUpdateReferralStatus(
      {
        onSuccess: () => {
          saveSponsorPatientId();
          closeModal();
          if (
            (selectedStatus === ReferralStatus.CONSENTED ||
              parentStatus === ReferralStatus.DROPPED) &&
            referral?.isVisit601Completed === null &&
            referral?.study?.has601Visit === true
          ) {
            openModal({
              modalType: 'PatientVisit601StatusReminder',
              modalProps: {
                referralId: referral?.id as string,
              },
            });
          }
          if (showNotificationToUser) {
            getStudySitePerformanceAPI({
              studyId: referral?.study.id as string,
              siteId: referral?.site?.id as string,
            });
          }
        },
      },
      refetchHistory,
      refetchProtocol
    );

  const { updateExternalStatus, isLoading: isExternalUpdateLoading } =
    Services.Referrals.useUpdateExternalReferralStatus({
      onSuccess: () => {
        closeModal();
      },
    });

  const { updateCallbackTime, isLoading: isUpdateCallbackTimeLoading } =
    Services.Referrals.useUpdateCallbackTime(
      {
        onSuccess: closeModal,
      },
      refetchHistory
    );

  const [isAppointmentScheduled, setIsAppointmentScheduled] =
    useState<boolean>(false);

  useEffect(() => {
    setIsAppointmentScheduled(
      selectedStatus === ReferralStatus.APPOINTMENT_SCHEDULED
    );
  }, [isAppointmentScheduled, selectedStatus]);

  const isButtonDisabled = (() => {
    if (isAppointmentScheduled) {
      const hasSelectedAppointment =
        appointmentScheduleDate && appointmentScheduleTime;
      const hasChangedAppointment = !(
        initialAppointmentTime === appointmentScheduleTime &&
        initialAppointmentDate === appointmentScheduleDate
      );
      return !hasSelectedAppointment || !hasChangedAppointment;
    }

    if (
      selectedStatus === ReferralStatus.CONSENTED ||
      selectedStatus === ReferralStatus.WAITING_FOR_INFORMATION
    ) {
      const isConsentedDateUnChanged = consentedDate
        ? currentConsentedDate === new Date(consentedDate)?.toISOString()
        : true;
      const isProtocolUnChanged = selectedProtocol
        ? protocolsData?.selectedProtocol?.id === selectedProtocol.id
        : true;
      const isAllDataSelected = protocolsData?.protocolList.length
        ? Boolean(selectedProtocol?.id && consentedDate)
        : Boolean(consentedDate);

      const isSponsorIDUnchanged =
        sponsorPatientID === referral?.sponsorPatientID;

      return isConsentedDateUnChanged &&
        isSponsorIDUnchanged &&
        isProtocolUnChanged
        ? true
        : !isAllDataSelected;
    }

    if (
      (selectedStatus ===
        ReferralStatus.DROPPED__FAILED_IN_EXCLUSION_CRITERIA &&
        failedIECriteriaDropReasons.length) ||
      (selectedStatus === ReferralStatus.DROPPED__NOT_INTERESTED &&
        notInterestedDropReasons.length)
    ) {
      return !selectedDropReason;
    }

    if (parentStatus) {
      return selectedStatus === parentStatus;
    }

    return selectedStatus === status;
  })();

  const updateConcented = () => {
    if (!selectedProtocol?.id) {
      updateStatus({
        id,
        status: selectedStatus as ReferralStatusType,
        appointmentDate:
          isAppointmentScheduled && appointmentScheduleDate
            ? toIsoInSiteTimeZone(appointmentScheduleDate as Date)
            : undefined,
        consentedDate: consentedDate?.toISOString(),
        isVisited: Boolean(currentIsVisited),
      });
      return;
    }

    updateStatus({
      id,
      status: selectedStatus as ReferralStatusType,
      appointmentDate:
        isAppointmentScheduled && appointmentScheduleDate
          ? toIsoInSiteTimeZone(appointmentScheduleDate as Date)
          : undefined,
      protocolInfoDto: selectedProtocol,
      consentedDate: consentedDate?.toISOString(),
      isVisited: Boolean(currentIsVisited),
      dropReasonId: selectedDropReason,
    });
  };

  const updateCallBackTime = (): void => {
    if (!appointmentScheduleDate || !siteTimeZoneName) return;
    const start = convertDateToTargetTimeZone(
      appointmentScheduleDate,
      siteTimeZoneName
    ).toISOString();
    const appointmentScheduledEndDate = convertDateToTargetTimeZone(
      addMinutes(appointmentScheduleDate, 30),
      siteTimeZoneName
    ).toISOString();
    updateCallbackTime({
      referralId: id as string,
      callbackTimeslotDto: {
        start,
        end: appointmentScheduledEndDate,
      },
    });
  };

  const handleForceStatusUpdate = (forcedStatusUpdate: ReferralStatusType) => {
    if (isExternalReferral) {
      updateExternalStatus({
        id: id as string,
        status: forcedStatusUpdate,
        appointmentDate:
          forcedStatusUpdate === ReferralStatus.APPOINTMENT_SCHEDULED &&
          appointmentScheduleDate
            ? toIsoInSiteTimeZone(appointmentScheduleDate as Date)
            : undefined,
        consentedDate: consentedDate?.toISOString(),
        isVisited: Boolean(currentIsVisited),
      });
      return;
    }

    if (
      forcedStatusUpdate === ReferralStatus.APPOINTMENT_SCHEDULED &&
      callbackTimeslotSiteSelectedEnabled
    ) {
      updateCallBackTime();
      return;
    }

    if (
      forcedStatusUpdate === ReferralStatus.APPOINTMENT_SCHEDULED &&
      !callbackTimeslotSiteSelectedEnabled
    ) {
      updateStatus({
        id,
        status: forcedStatusUpdate,
        appointmentDate: appointmentScheduleDate
          ? toIsoInSiteTimeZone(appointmentScheduleDate as Date)
          : undefined,
        isVisited: Boolean(currentIsVisited),
      });

      return;
    }

    if (forcedStatusUpdate === ReferralStatus.CONSENTED) {
      updateConcented();
    }

    updateStatus({
      id,
      status: forcedStatusUpdate,
      isVisited: Boolean(currentIsVisited),
    });
  };

  const handleUpdateStatusClick = () => {
    if (isExternalReferral) {
      updateExternalStatus({
        id: id as string,
        status: selectedStatus as ReferralStatusType,
        appointmentDate:
          isAppointmentScheduled && appointmentScheduleDate
            ? toIsoInSiteTimeZone(appointmentScheduleDate as Date)
            : undefined,
        consentedDate: consentedDate?.toISOString(),
        isVisited: Boolean(currentIsVisited),
      });
      return;
    }

    if (
      selectedStatus === ReferralStatus.APPOINTMENT_SCHEDULED &&
      callbackTimeslotSiteSelectedEnabled
    ) {
      updateCallBackTime();
    } else if (selectedStatus === 'CONSENTED') {
      updateConcented();
    } else {
      updateStatus({
        id,
        status: selectedStatus as ReferralStatusType,
        appointmentDate:
          isAppointmentScheduled && appointmentScheduleDate
            ? toIsoInSiteTimeZone(appointmentScheduleDate as Date)
            : undefined,
        isVisited: Boolean(currentIsVisited),
        dropReasonId: selectedDropReason,
      });
    }
  };

  const handleNextClick = () => {
    setCanNextStep(false);

    if (parentStatus === 'REVERT' && possibleNextStep) {
      handleForceStatusUpdate(possibleNextStep as ReferralStatusType);
      return;
    }

    if (
      (parentStatus === ReferralStatus.CONSENTED ||
        parentStatus === ReferralStatus.WAITING_FOR_INFORMATION) &&
      !currentIsVisited
    ) {
      setParentStatus('IS_APPOINTMENT_HAPPEND');
      nextStep();
      return;
    }

    if (parentStatus === 'IS_APPOINTMENT_HAPPEND' && currentIsVisited) {
      setParentStatus(possibleNextStep || ReferralStatus.CONSENTED);
      return;
    }

    if (
      parentStatus === ReferralStatus.DROPPED &&
      (selectedStatus ===
        ReferralStatus.DROPPED__FAILED_IN_EXCLUSION_CRITERIA ||
        selectedStatus === ReferralStatus.DROPPED__NOT_INTERESTED)
    ) {
      setParentStatus('DROP_REASON');
      nextStep();
      return;
    }

    if (parentStatus === 'IS_APPOINTMENT_HAPPEND' && !currentIsVisited) {
      setParentStatus('IS_NEW_APPOINTMENT_NEEDED');
      return;
    }

    if (
      parentStatus === 'IS_NEW_APPOINTMENT_NEEDED' &&
      isNewAppointmentNeeded
    ) {
      setParentStatus(ReferralStatus.APPOINTMENT_SCHEDULED);
      setSelectedStatus(ReferralStatus.APPOINTMENT_SCHEDULED);
      return;
    }

    if (
      parentStatus === 'IS_NEW_APPOINTMENT_NEEDED' &&
      !isNewAppointmentNeeded
    ) {
      setSelectedStatus(ReferralStatus.DROPPED__NO_SHOW);
      handleForceStatusUpdate(ReferralStatus.DROPPED__NO_SHOW);
      return;
    }

    if (canNextStep) {
      nextStep();
      setCanNextStep(false);
    }
  };

  const handleBackClick = (): void => {
    prevStep();
    setSelectedDropReason('');
    setParentStatus('');
    setCurrentIsVisited(null);
    setSelectedStatus(status as ReferralStatusType);
    setSelectedProtocol(null);
    setConsentedDate(null);
    setSponsorPatientID(referral?.sponsorPatientID || '');
  };

  return (
    <Flex
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
      gap={3}
    >
      <ShouldRender when={activeStep >= 2}>
        <Button
          onClick={handleBackClick}
          variant="tertiary"
          icon={<ArrowIcon direction="left" />}
        >
          <FormattedMessage
            id="is.update_patient_status_modal.back_button"
            defaultMessage="Back"
          />
        </Button>
      </ShouldRender>
      <Flex
        gap={3}
        sx={{
          width: '100%',
          justifyContent: 'flex-end',
        }}
      >
        <Button onClick={() => closeModal()}>
          <FormattedMessage
            id="is.update_protocol_modal.cancel_button"
            defaultMessage="Cancel"
          />
        </Button>
        <ShouldRender when={Boolean(canNextStep)}>
          <Button onClick={() => handleNextClick()}>
            <FormattedMessage
              id="is.update_patient_status_modal.next_button"
              defaultMessage="Next"
            />
          </Button>
        </ShouldRender>
        <ShouldRender when={Boolean(!canNextStep)}>
          <Button
            disabled={isButtonDisabled}
            loading={
              isLoading ||
              isUpdateCallbackTimeLoading ||
              isExternalUpdateLoading
            }
            onClick={() => handleUpdateStatusClick()}
          >
            <FormattedMessage
              id="is.update_patient_status_modal.update_button"
              defaultMessage="Update status"
            />
          </Button>
        </ShouldRender>
      </Flex>
    </Flex>
  );
};
